/**
 * Session Scope
 *
 * The one moment at which a signed-in account's client state is dropped.
 *
 * Account data lives in module singletons — stores, service caches, platform
 * caches, OS surfaces — and none of it restarts when a session ends. Seven
 * `authStore` paths end one, and each used to clear only what someone had
 * remembered to clear on that path. Instead, `authStore` watches its own
 * `user.username` and calls `changeSessionScope` whenever the account changes:
 * on the way out (A → none), on the way in (none → B), and on a swap that
 * never flips `isAuthenticated` (A → B). Anything holding one account's data
 * registers how to drop it here, and the boundary reaches every path — the
 * ones written today and the next one added.
 *
 * Like store-sync, this module imports nothing but types, so a store, a
 * service and platform code can all register without `shared` importing any
 * of them.
 *
 * Rules for a registered clear (docs/roadmap/session-scoped-state.md):
 *
 * 1. Local and synchronous, or fire-and-forget. The boundary runs inside the
 *    `set()` that changed the user; anything that must reach the server with
 *    the old session — revoking a push token, a bearer — runs BEFORE it.
 * 2. Idempotent. It runs on every change, both ways, so at least twice per
 *    account switch.
 * 3. The OS only on the way out (`previous !== null`). Mobile's cold-start
 *    rehydrate fires none → A, and clearing the notification centre there
 *    would dismiss A's own notifications and swallow the push tap that
 *    launched the app.
 * 4. Account data only, not device settings.
 *
 * A role switch (client ↔ trainer) keeps the account but reshapes its data:
 * the dashboard, the roster, the reports. `authStore` calls `changeRoleScope`
 * when `user.role` changes under the same username, and every clear runs
 * again, except those registered with `{ scope: 'account' }`: the OS
 * surfaces and sign-in identity, which belong to the account, not its role.
 */

// =============================================================================
// TYPES
// =============================================================================

/** Whose session ended and whose began. `null` is "nobody signed in". */
export interface SessionChange {
  previous: string | null;
  next: string | null;
}

export type SessionScopedClear = (change: SessionChange) => void;

export interface SessionScopedOptions {
  /**
   * `data` (the default) clears at every boundary, a role switch included.
   * `account` clears only when the account itself changes: for OS surfaces
   * and sign-in identity, which a role switch leaves where they are.
   */
  scope?: 'data' | 'account';
}

interface Registration {
  clear: SessionScopedClear;
  scope: 'data' | 'account';
}

// =============================================================================
// REGISTRY
// =============================================================================

// Named, so a re-import or a hot reload replaces a registration rather than
// duplicating it.
const registry = new Map<string, Registration>();

// Bumped at every boundary. A response to a request made under an older epoch
// belongs to a finished session and must not be written anywhere.
let epoch = 0;

// =============================================================================
// REGISTRATION
// =============================================================================

/**
 * Register how one piece of account-scoped state is dropped.
 *
 * @param name - A stable name for the registrant (the store or cache), which
 *   a later registration under the same name replaces
 * @param clear - Called at every boundary with who is leaving and who is
 *   arriving
 * @param options - `{ scope: 'account' }` to sit out a role switch
 */
export const registerSessionScoped = (
  name: string,
  clear: SessionScopedClear,
  options: SessionScopedOptions = {}
): void => {
  registry.set(name, { clear, scope: options.scope ?? 'data' });
};

/**
 * Forget a registration. For tests and for platform code whose lifetime is
 * shorter than the app's (a provider that unmounts).
 */
export const unregisterSessionScoped = (name: string): void => {
  registry.delete(name);
};

/** The names registered so far. For the guard-rail test. */
export const listSessionScoped = (): string[] => [...registry.keys()];

// =============================================================================
// THE BOUNDARY
// =============================================================================

/**
 * The current session epoch. Capture it before a request and compare after:
 * a different number means a boundary passed while the request was in flight.
 */
export const getSessionEpoch = (): number => epoch;

/**
 * Cross the boundary: bump the epoch and run every registered clear.
 *
 * Called by `authStore` from its own subscription. Not for anything else to
 * call — a session ends by changing the user, and the boundary follows.
 */
export const changeSessionScope = (change: SessionChange): void => {
  runClears(change, 'account');
};

/**
 * Cross a role boundary: the same account, now a client or now a trainer.
 * Bumps the epoch and runs every `data` clear with the account as both sides
 * of the change. Called by `authStore`, like `changeSessionScope`.
 */
export const changeRoleScope = (username: string): void => {
  runClears({ previous: username, next: username }, 'data');
};

const runClears = (
  change: SessionChange,
  boundary: 'data' | 'account'
): void => {
  epoch += 1;

  for (const [name, { clear, scope }] of registry) {
    if (boundary === 'data' && scope === 'account') continue;
    try {
      clear(change);
    } catch (error) {
      // One registrant failing must not leave the rest of the account's data
      // in place.
      console.warn(`[session] ${name} could not clear`, error);
    }
  }
};
